import {
  HelpDeskReference,
  QrStatus,
  SigningMethod,
} from '../enums/const-enums';
import { getBaseUrl, logd } from '../utils/utils';

export abstract class Endpoint {
  protected baseUrl: string;

  protected constructor() {
    this.baseUrl = getBaseUrl();
  }
}

export enum AltFlowType {
  CHANGE_ACCOUNT = '01',
}

export class DebitToken implements AppModel.DebitCard {
  account_number: string;
  card_number: string;

  constructor(accountNumber: string, cardNumber: string) {
    this.account_number = accountNumber;
    this.card_number = cardNumber;
  }
}

export class CreditCardToken implements AppModel.CreditCard {
  credit_card_type: string;
  card_number: string;

  constructor(creditCardType: string, cardNumber: string) {
    this.credit_card_type = creditCardType;
    this.card_number = cardNumber;
  }
}

export class SignInitRequest implements AppModel.SignInitRequest {
  accepted_methods: SigningMethod[];
  preferred_method: SigningMethod;
  abs_path: string;

  constructor(
    acceptedMethods: SigningMethod[],
    preferredMethod: SigningMethod,
    absPath: string,
  ) {
    this.accepted_methods = acceptedMethods;
    this.preferred_method = preferredMethod;
    this.abs_path = absPath;
  }
}

export class SignInitResponse {
  usable_methods: Array<SigningMethod>;
  selected_method: SigningMethod;
  challenge_data: AppModel.ChallengeData;
  summary: AppModel.Summary;
  display_type: string;
  signing_limitation_info?: AppModel.SigningLimitationInfo;

  constructor(jsonResponse: SignInitResponse) {
    Object.assign(this, jsonResponse);
  }
}

export class GetChallengeRequest implements AppModel.GetChallengeRequest {
  accepted_methods: SigningMethod[];
  preferred_method: SigningMethod;
  readonly force_new_challenge: boolean = true;
  debit_token?: AppModel.DebitCard;
  credit_token?: AppModel.CreditCard;

  constructor(
    acceptedMethods: SigningMethod[],
    preferredMethod: SigningMethod,
    debitToken: AppModel.DebitCard = undefined,
    creditToken: AppModel.CreditCard = undefined,
  ) {
    this.accepted_methods = acceptedMethods;
    this.preferred_method = preferredMethod;
    this.debit_token = debitToken;
    this.credit_token = creditToken;
  }
}

export class GetChallengeResponse {
  usable_methods: Array<SigningMethod>;
  selected_method: SigningMethod;
  challenge_data: AppModel.ChallengeData;
  helpdesk_ref?: HelpDeskReference;
  signing_limitation_info?: AppModel.SigningLimitationInfo;

  constructor(jsonResponse: GetChallengeResponse) {
    Object.assign(this, jsonResponse);
  }
}

export class SignSubmitRequest {
  response_info: AppModel.ResponseInfo;

  constructor(responseInfo: AppModel.ResponseInfo) {
    this.response_info = responseInfo;
  }
}

export class SignCancelRequest {
  alt_flow_used: boolean;

  constructor(isAltFlowUsed: boolean) {
    this.alt_flow_used = isAltFlowUsed;
  }
}

export class AppChallenge implements AppModel.ChallengeData {
  readonly objectType: string = 'app_challenge';
  encrypted: AppModel.Encrypted;
  json_extra: AppModel.JsonExtra;
  submit_url: string;

  constructor(
    submitUrl: string,
    encrypted: AppModel.Encrypted,
    json: AppModel.JsonExtra,
  ) {
    this.encrypted = encrypted;
    this.json_extra = json;
    this.submit_url = submitUrl;
  }
}

export class OobChallenge implements AppModel.ChallengeData {
  readonly objectType: string = 'oob_challenge';
  phone_number: string;
  helpdesk_ref?: string;

  constructor(phoneNumber: string, helpdeskRef: string = null) {
    this.phone_number = phoneNumber;
    this.helpdesk_ref = helpdeskRef;
  }
}

export class OneClickIdChallenge implements AppModel.ChallengeData {
  readonly objectType: string = 'one_click_id_challenge';
  challenge_id: string;

  constructor(challengeId: string) {
    this.challenge_id = challengeId;
  }
}
export class OobResponseInfo implements AppModel.ResponseInfo {
  readonly objectType: string = 'oob_response';
  sign_code: string;

  constructor(signCode: string) {
    this.sign_code = signCode;
  }
}

export class QrChallenge implements AppModel.ChallengeData {
  readonly objectType: string = 'qr_challenge';
  qr_id: string;
  is_qr_high_signing = false;

  constructor(qrId: string) {
    this.qr_id = qrId;
  }
}

export class TinWebChallenge implements AppModel.ChallengeData {
  readonly objectType: string = 'tin_web_challenge';
  challenge_id: string;
  ecdhe_server_pubkey: AppModel.EcdhePubKey;

  constructor(challengeId: string) {
    this.challenge_id = challengeId;
  }
}

export class RaboScannerChallenge implements AppModel.ChallengeData {
  readonly objectType: string = 'rabo_scanner_challenge';
  altFlowType: AltFlowType;
  token_info: AppModel.TokenInfo;
  uri: string;
  validity?: number;

  constructor(tokenInfo: AppModel.TokenInfo, uri: string) {
    this.token_info = tokenInfo;
    this.uri = uri;
  }
}

export class RandomReaderChallenge implements AppModel.ChallengeData {
  readonly objectType: string = 'random_reader_challenge';
  altFlowType: AltFlowType;
  token_info: AppModel.TokenInfo;
  challenges: Array<AppModel.ReaderChallenge>;

  constructor(
    tokenInfo: AppModel.TokenInfo,
    challenges: Array<AppModel.ReaderChallenge>,
  ) {
    this.token_info = tokenInfo;
    this.challenges = challenges;
  }
}

export class OneClickResponseInfo implements AppModel.ResponseInfo {
  readonly objectType: string = 'one_click_id_response';
  challenge_id: string;
  constructor(challengeId: string) {
    this.challenge_id = challengeId;
  }
}

export class TinWebResponseInfo implements AppModel.ResponseInfo {
  readonly objectType: string = 'tin_web_response';
  ecdhe_client_pubkey: AppModel.EcdhePubKey;
  access_code_encrypted: AppModel.Encrypted;
  challenge_id: string;
  constructor(
    ecdheClientPubkey: AppModel.EcdhePubKey,
    accessCodeEncrypted: AppModel.Encrypted,
    challengeId: string,
  ) {
    this.ecdhe_client_pubkey = ecdheClientPubkey;
    this.access_code_encrypted = accessCodeEncrypted;
    this.challenge_id = challengeId;
  }
}

export class EcdheClientPubKey implements AppModel.EcdhePubKey {
  curve: string;
  x: string;
  y: string;
  constructor(curve: string, x: string, y: string) {
    this.curve = curve;
    this.x = x;
    this.y = y;
  }
}

export class AccessCodeEncrypted implements AppModel.Encrypted {
  method: string;
  init_vector: string;
  payload: string;
  constructor(method: string, initVector: string, payload: string) {
    this.method = method;
    this.init_vector = initVector;
    this.payload = payload;
  }
}

export class AccessCodeEncryptedInfo {
  ecdhe_client_pubkey: AppModel.EcdhePubKey;
  access_code_encrypted: AppModel.Encrypted;
  constructor(
    ecdheClientPubkey: AppModel.EcdhePubKey,
    accessCodeEncrypted: AppModel.Encrypted,
  ) {
    this.ecdhe_client_pubkey = ecdheClientPubkey;
    this.access_code_encrypted = accessCodeEncrypted;
  }
}

export class RaboScannerResponseInfo implements AppModel.ResponseInfo {
  readonly objectType: string = 'rabo_scanner_response';
  sign_code: string;

  constructor(signCode: string) {
    this.sign_code = signCode;
  }
}

export class RandomReaderResponseInfo implements AppModel.ResponseInfo {
  readonly objectType: string = 'random_reader_response';
  sign_code: string;
  account_number: string;
  card_number: string;

  constructor(signCode: string, accountNumber: string, cardNumber: string) {
    this.sign_code = signCode;
    this.account_number = accountNumber;
    this.card_number = cardNumber;
  }
}

export class ContinueResponse {
  continue_url?: string;
  continue_content?: string;
  constructor(jsonResponse: ContinueResponse) {
    Object.assign(this, jsonResponse);
  }
  public redirect?(): void {
    if (this.continue_url) {
      window.location.assign(this.continue_url);
    } else if (this.continue_content) {
      // Reference : https://stackoverflow.com/questions/53099215/document-write-is-not-working-under-ie-and-edge
      document.write(this.continue_content);
    } else {
      logd(() => console.warn('Continue response is not valid!'));
    }
  }
}

export class PollQrRequest implements AppModel.PollQrRequest {
  qr_id: string;

  constructor(qrId: string) {
    this.qr_id = qrId;
  }
}

export class PollQrResponse {
  qr_status: QrStatus;
  continue_url?: string;
  continue_content?: string;

  constructor(jsonResponse: PollQrResponse) {
    Object.assign(this, jsonResponse);
  }
}
